import React from 'react';
import { useQuery } from 'react-query';
import {
  Modal,
  Typography,
  Backdrop,
  Grid,
  RadioGroup,
  Radio,
  FormLabel,
  FormControlLabel,
  FormControl,
  FormHelperText,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import * as yup from 'yup';
import { debounce } from 'lodash';
import {
  postActivity,
  postActivityFilterOptions,
  getYesOrNotOption,
  getActivityMaintenanceType,
} from 'services/activity';
import { getClusters } from 'services/cluster';
import { postInstallationOptions } from 'services/installation';

import { postEquipmentOptionsByInstallation } from 'services/equipment';
import { postExecutorOptions, getExecutorOptions } from 'services/executor';
import ConfirmModal from 'components/atoms/ConfirmModal';
import InputDuration from 'components/atoms/InputDuration';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import AutocompleteTree from 'components/atoms/AutocompleteTree';
import { IEquipmentFilterTree } from 'interfaces/Equipment';
import { IAutocompleteTreeRef } from 'components/atoms/AutocompleteTree/types';
import { IModalAddProps } from './types';
import useStyles from './style';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const SEARCH_GUIDE_TEXT = 'Digite 2 Caracteres ou mais para pesquisar';
const LIMIT_CARACTERES = 'O limite é de 250 caracteres';

const FORM_ID = 'ADD_PLANNING_ACTIVITY';

const MIN_DATE = dayjs(dayjs().format('YYYY-MM-DD'));
const MAX_DATE = dayjs('2100-01-01');

dayjs.extend(isoWeek);
dayjs.extend(duration);

type IOption = {
  value: string | number;
  label: string;
};

const LoadingInputEndAdornment = ({ loading = false }) =>
  loading ? (
    <InputAdornment position="start">
      <CircularProgress color="inherit" size={25} />
    </InputAdornment>
  ) : null;

const OPTION = 'Opcional';

const ModalAdd = ({ open, onClose, children, reloadData }: IModalAddProps) => {
  const classes = useStyles();

  const equipmentTreeRef = React.useRef<IAutocompleteTreeRef>(null);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [fieldsSearchText, setFieldsSearchText] = React.useState<{
    [key: string]: string;
  }>({});
  const [activityOptions, setActivityOptions] = React.useState<IOption[]>([]);
  const [clusterOptions, setClusterOptions] = React.useState<IOption[]>([]);
  const [installationOptions, setInstallationOptions] = React.useState<
    IOption[]
  >([]);
  const [typeMaintenanceOptions, setTypeMaintenanceOptions] = React.useState<
    IOption[]
  >([]);
  const [equipmentOptions, setEquipmentOptions] = React.useState<
    IEquipmentFilterTree[]
  >([]);
  const [executorOptions, setExecutorOptions] = React.useState<IOption[]>([]);

  const [outageOptions, setOutageOptions] = React.useState<IOption[]>([]);

  const [utilizationOptions, setUtilizationOptions] = React.useState<IOption[]>(
    [],
  );

  const defaultExpandedEquipments = React.useMemo(() => {
    if (equipmentOptions.length) {
      return [equipmentOptions[0].id];
    }

    return undefined;
  }, [equipmentOptions]);

  const validationSchema = yup.object({
    activityNameOrId: yup.string().required(DEFAULT_OK_TEXT),
    clusterId: yup.string().required(DEFAULT_OK_TEXT),
    installationId: yup.string().required(DEFAULT_OK_TEXT),
    maintenanceId: yup.string().required(DEFAULT_OK_TEXT),
    equipmentId: yup.string().required(DEFAULT_OK_TEXT),
    startPrev: yup.mixed().required(DEFAULT_OK_TEXT),
    observationField: yup.string().max(250, LIMIT_CARACTERES).nullable(),
    durationPrev: yup.string().required(DEFAULT_OK_TEXT),
    providerName: yup.string().required(DEFAULT_OK_TEXT),
    frequency: yup.number().positive(DEFAULT_ERROR_TEXT).nullable(),
    outage: yup.string().required(DEFAULT_OK_TEXT),
    utilization: yup.string().required(DEFAULT_OK_TEXT),
    planNumber: yup.string().required().typeError(DEFAULT_OK_TEXT),
    completionPrev: yup.mixed().required(DEFAULT_OK_TEXT),
    // completionPrev: yup.mixed().required(DEFAULT_OK_TEXT),
    // durationPrev: yup.string(),
    // providerName: yup.string().required(DEFAULT_OK_TEXT),
    // frequency: yup.number().positive(DEFAULT_ERROR_TEXT).nullable(),
    // outage: yup.string().required(DEFAULT_OK_TEXT),
    // utilization: yup.string().required(DEFAULT_OK_TEXT),
    // planNumber: yup.string().required().typeError(DEFAULT_OK_TEXT)
    // .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
    //   const test = dayjs(value);
    //   return (
    //     dayjs(value).isValid() &&
    //     (test.isSame(MIN_DATE) || test.isAfter(MIN_DATE))
    //   );
    // }),
    // eslint-disable-next-line func-names
    // .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
    //   const test = dayjs(value);
    //   // eslint-disable-next-line react/no-this-in-sfc
    //   const { startPrev } = this.parent;
    //   return (
    //     dayjs(value).isValid() &&
    //     (test.isSame(startPrev) || test.isAfter(startPrev))
    //   );
    // }),
  });

  const formik = useFormik({
    initialValues: {
      activityNameOrId: '',
      activityName: '',
      activityId: '',
      clusterId: '',
      installationId: '',
      maintenanceId: '',
      equipmentId: '',
      startPrev: null,
      completionPrev: null,
      durationPrev: '',
      providerName: '',
      frequency: null,
      outage: '',
      utilization: '',
      week: dayjs().isoWeek().toString(),
      planNumber: dayjs().year(),
      completionPercentage: 0,
      observationField: '',
    },
    validationSchema,
    onSubmit: formValues => {
      // TODO: verificar serviço no backend swagger
      const { activityNameOrId, ...values } = formValues;

      if (typeof activityNameOrId === 'string') {
        // então estou criando uma atividade
        values.activityName = activityNameOrId;
      } else {
        // se não estou referenciando uma atividade
        values.activityId = activityNameOrId;
      }

      let durationPrev = 0;
      if (formValues.durationPrev) {
        const [hours, minutes] = formValues.durationPrev.split(':');

        durationPrev = Number(
          dayjs
            .duration({
              hours: Number(hours),
              minutes: Number(minutes),
            })
            .asHours()
            .toFixed(2),
        );
      }

      // se for enviado com sucesso
      setLoading(true);
      postActivity({ ...values, durationPrev })
        .then(() => {
          // solicita um reload de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoading(false));
    },
  });

  // Só posso fazer queries após abrir o modal
  const enableQueries = React.useMemo(() => open, [open]);

  const enableQueryActivityOptions = React.useMemo(() => {
    return (
      enableQueries &&
      Boolean(formik.values.installationId) &&
      Boolean(
        fieldsSearchText?.activityNameOrId &&
          fieldsSearchText?.activityNameOrId.length >= 2,
      )
    );
  }, [
    enableQueries,
    fieldsSearchText?.activityNameOrId,
    formik.values.installationId,
  ]);

  const enableClusterOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableInstallationOptions = React.useMemo(() => {
    return enableQueries && Boolean(formik.values.clusterId);
  }, [enableQueries, formik.values.clusterId]);

  const enableTypeMaintenanceOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableQueryEquipmentOptions = React.useMemo(() => {
    return enableQueries && Boolean(formik.values.installationId);
  }, [enableQueries, formik.values.installationId]);

  const enableExecutorAllOptions = React.useMemo(() => {
    return enableQueries;
  }, [enableQueries]);

  const enableExecutorOptions = React.useMemo(() => {
    return (
      enableQueries &&
      Boolean(
        fieldsSearchText?.providerName &&
          fieldsSearchText?.providerName.length >= 2,
      )
    );
  }, [enableQueries, fieldsSearchText?.providerName]);

  const enableOutageOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableUtilizationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryActivityOptions = useQuery({
    queryKey: [
      'ActivityOptionsContext',
      [fieldsSearchText?.activityNameOrId],
      formik?.values.installationId,
    ],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.activityNameOrId,
        installationId: formik?.values.installationId,
      };
      const { data } = await postActivityFilterOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setActivityOptions(data);
    },
    enabled: enableQueryActivityOptions,
  });

  const queryClusterOptions = useQuery({
    queryKey: ['clusterOptionsContext'],
    queryFn: async () => {
      const { data } = await getClusters();

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setClusterOptions(data);
    },
    enabled: enableClusterOptions,
  });

  const queryInstallationOptions = useQuery({
    queryKey: ['installationOptionsContext', [formik.values.clusterId]],
    queryFn: async () => {
      const params = { clusterIds: [formik.values?.clusterId] };
      const { data } = await postInstallationOptions(params);
      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setInstallationOptions(data);
    },
    enabled: enableInstallationOptions,
  });

  const queryMaintenanceTypeOptions = useQuery({
    queryKey: ['maintenanceTypeOptions'],
    queryFn: async () => {
      const { data } = await getActivityMaintenanceType({});

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setTypeMaintenanceOptions(data);
    },
    enabled: enableTypeMaintenanceOptions,
  });

  const queryEquipmentOptions = useQuery({
    queryKey: ['EquipmentOptionsContext', [formik?.values.installationId]],
    queryFn: async () => {
      const params = {
        installationIds: [formik?.values.installationId],
      };
      const { data } = await postEquipmentOptionsByInstallation(params);
      return data;
    },
    onSuccess: data => {
      setEquipmentOptions(data.result);
    },
    enabled: enableQueryEquipmentOptions,
  });

  const queryExecutorAllOptions = useQuery({
    queryKey: ['ExecutorAllOptionsContext'],
    queryFn: async () => {
      const { data } = await getExecutorOptions();
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.description,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setExecutorOptions(data);
    },
    enabled: enableExecutorAllOptions,
  });

  const queryExecutorOptions = useQuery({
    queryKey: ['ExecutorOptionsContext', [fieldsSearchText?.providerName]],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.providerName,
      };
      const { data } = await postExecutorOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.description,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setExecutorOptions(data);
    },
    enabled: enableExecutorOptions,
  });

  const queryOutageOptions = useQuery({
    queryKey: ['outageOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setOutageOptions(data);
    },
    enabled: enableOutageOptions,
  });

  const queryUtilizationOptions = useQuery({
    queryKey: ['utilizationOptionsContext'],
    queryFn: async () => {
      // TODO: trocar para rota especifica do filtro
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setUtilizationOptions(data);
    },
    enabled: enableUtilizationOptions,
  });

  const onCloseModal = React.useCallback(() => {
    if (formik.dirty) {
      setOpenConfirmModal(true);
    } else {
      onClose();
    }
  }, [formik.dirty, onClose]);

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const clearFieldSearchTextChange = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;
      setFieldsSearchText(cv => ({ ...cv, [name]: '' }));
    },
    [],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const getInputValue = (
    inputOptions: IOption[] | undefined,
    selectedValue: string | number | undefined,
  ) => inputOptions?.find(item => item.value === selectedValue)?.label || '';

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, inputLoading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={inputLoading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  const filter = createFilterOptions<IOption>();

  React.useEffect(() => {
    // se existir alteracoes no campo & estiver com a modal fechada
    if (!open && formik.dirty) {
      // então: reset form quando eu fecho o modal,
      formik.resetForm();

      // caso esteja fechado e existir valores nos campos listas eles devem ser limpos
      if (clusterOptions.length) setActivityOptions([]);
      if (activityOptions.length) setClusterOptions([]);
      if (installationOptions.length) setInstallationOptions([]);
      if (typeMaintenanceOptions.length) setTypeMaintenanceOptions([]);
      if (equipmentOptions.length) setEquipmentOptions([]);
      if (executorOptions.length) setExecutorOptions([]);
      if (outageOptions.length) setOutageOptions([]);
      if (utilizationOptions.length) setUtilizationOptions([]);
      if (Object.keys(fieldsSearchText).length) setFieldsSearchText({});
      if (equipmentTreeRef.current) equipmentTreeRef.current.onReset();
    }
  }, [
    activityOptions.length,
    clusterOptions.length,
    equipmentOptions.length,
    executorOptions.length,
    fieldsSearchText,
    formik,
    installationOptions.length,
    open,
    outageOptions.length,
    typeMaintenanceOptions.length,
    utilizationOptions.length,
  ]);

  const renderForm = React.useCallback(
    () => (
      <form
        id={FORM_ID}
        className={classes.bodyContent}
        onSubmit={formik.handleSubmit}
      >
        <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
          <Grid className={classes.gridContent} container spacing={2}>
            {/* START ROW 1 */}
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Autocomplete
                  id="clusterId"
                  className={classes.inputList}
                  options={clusterOptions}
                  onChange={(e, selected) => {
                    // sempre que alterar o cluster selecionado
                    // tem que limpar a instalação selecionada
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      installationId: '',
                      // activityNameOrId: '',
                      equipmentId: '',
                    }));

                    if (equipmentTreeRef.current)
                      equipmentTreeRef.current.onReset();

                    return handleAutoCompleteChange(
                      'clusterId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'clusterId',
                      label: 'Cluster',
                      error:
                        formik.touched.clusterId &&
                        Boolean(formik.errors.clusterId),
                      helperText:
                        (formik.touched.clusterId && formik.errors.clusterId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryClusterOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  id="installationId"
                  className={classes.inputList}
                  options={installationOptions}
                  disabled={!formik.values?.clusterId}
                  disableClearable={!formik.values.installationId}
                  inputValue={getInputValue(
                    installationOptions,
                    formik.values?.installationId,
                  )}
                  onChange={(e, selected) => {
                    formik.setValues(oldValues => ({
                      ...oldValues,
                      // activityNameOrId: '',
                      equipmentId: '',
                    }));

                    if (equipmentTreeRef.current)
                      equipmentTreeRef.current.onReset();

                    setEquipmentOptions([]);

                    return handleAutoCompleteChange(
                      'installationId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'installationId',
                      label: 'Instalação',
                      error:
                        formik.touched.installationId &&
                        Boolean(formik.errors.installationId),
                      helperText:
                        (formik.touched.installationId &&
                          formik.errors.installationId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryInstallationOptions?.isLoading,
                    );
                  }}
                />
              </Grid>

              <Grid item xs={2}>
                <Autocomplete
                  id="providerName"
                  className={classes.inputList}
                  options={executorOptions}
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'providerName',
                      selected as IOption,
                    );
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    clearFieldSearchTextChange(event);
                    queryExecutorAllOptions.refetch();
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue.length >= 2) {
                      filtered.push({
                        label: params.inputValue,
                        value: params.inputValue,
                      });
                    }

                    return filtered;
                  }}
                  renderInput={params => {
                    const extraProps = {
                      name: 'providerName',
                      label: 'Empresa',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.providerName &&
                        Boolean(formik.errors.providerName),
                      helperText:
                        (formik.touched.providerName &&
                          formik.errors.providerName) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      {
                        ...params,
                        ...extraProps,
                      },
                      queryExecutorOptions?.isLoading ||
                        queryExecutorAllOptions?.isLoading,
                    );
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <Autocomplete
                  id="activityNameOrId"
                  className={classes.inputList}
                  options={activityOptions}
                  disabled={!formik.values.providerName}
                  inputValue={
                    getInputValue(
                      activityOptions,
                      formik.values?.activityNameOrId,
                    ) || formik.values?.activityNameOrId
                  }
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'activityNameOrId',
                      selected as IOption,
                    );
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === 'reset') {
                      formik.setFieldValue('activityNameOrId', '');
                    } else {
                      formik.setFieldValue('activityNameOrId', newValue);
                    }
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={
                    !fieldsSearchText?.activityNameOrId
                      ? SEARCH_GUIDE_TEXT
                      : NO_OPTIONS_TEXT
                  }
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    // TODO: solicitar ao backend para desabilitar a criação de uma nova atividade
                    // no adicionar de planejamento
                    // Suggest the creation of a new value
                    // if (params.inputValue.length >= 3) {
                    //   filtered.push({
                    //     label: params.inputValue,
                    //     value: params.inputValue,
                    //   });
                    // }

                    return filtered;
                  }}
                  renderInput={params => {
                    const extraProps = {
                      name: 'activityNameOrId',
                      label: 'Título da atividade',
                      onChange: handleFieldSearchTextChange,
                      error:
                        formik.touched.activityNameOrId &&
                        Boolean(formik.errors.activityNameOrId),
                      helperText:
                        (formik.touched.activityNameOrId &&
                          formik.errors.activityNameOrId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      {
                        ...params,
                        ...extraProps,
                      },
                      queryActivityOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
            </Grid>
            {/* END ROW 1 */}

            {/* START ROW 2 */}
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <Autocomplete
                  id="maintenanceId"
                  className={classes.inputList}
                  options={typeMaintenanceOptions}
                  onChange={(e, selected) => {
                    return handleAutoCompleteChange(
                      'maintenanceId',
                      selected as IOption,
                    );
                  }}
                  getOptionLabel={option => String(option.label)}
                  getOptionSelected={getOptionSelected}
                  noOptionsText={NO_OPTIONS_TEXT}
                  renderInput={params => {
                    const extraProps = {
                      name: 'maintenanceId',
                      label: 'Tipo de manutenção',
                      error:
                        formik.touched.maintenanceId &&
                        Boolean(formik.errors.maintenanceId),
                      helperText:
                        (formik.touched.maintenanceId &&
                          formik.errors.maintenanceId) ||
                        DEFAULT_HELPER_TEXT,
                    };
                    return inputRenderer(
                      { ...params, ...extraProps },
                      queryMaintenanceTypeOptions?.isLoading,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <AutocompleteTree
                  id="equipmentId"
                  ref={equipmentTreeRef}
                  defaultExpanded={defaultExpandedEquipments}
                  name="equipmentId"
                  noOptionsText="Sem opções disponíveis"
                  className={classes.inputList}
                  disabled={!formik.values.installationId}
                  items={equipmentOptions}
                  label="Equipamento"
                  placeholder=""
                  extraTextFieldProps={{
                    error:
                      formik.touched.equipmentId &&
                      Boolean(formik.errors.equipmentId),
                    helperText:
                      (formik.touched.equipmentId &&
                        formik.errors.equipmentId) ||
                      DEFAULT_HELPER_TEXT,
                  }}
                  loading={queryEquipmentOptions?.isLoading}
                  onChange={(name, treeValue) => {
                    let safeValue = {
                      label: '',
                      value: '',
                    } as IOption;

                    if (treeValue) {
                      safeValue = {
                        label: treeValue.description,
                        value: treeValue.id,
                      };
                    }

                    handleAutoCompleteChange(name, safeValue);
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <KeyboardDatePicker
                  id="startPrev"
                  name="startPrev"
                  label="Início previsto"
                  inputVariant="outlined"
                  format="DD/MM/YY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  clearable
                  // minDate={MIN_DATE}
                  // maxDate={MAX_DATE}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  onChange={date => handleDateChange('startPrev', date)}
                  value={formik.values.startPrev}
                  error={
                    formik.touched.startPrev && Boolean(formik.errors.startPrev)
                  }
                  helperText={
                    (formik.touched.startPrev && formik.errors.startPrev) ||
                    DEFAULT_HELPER_TEXT
                  }
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={2}>
                <KeyboardDatePicker
                  id="completionPrev"
                  name="completionPrev"
                  label="Término previsto"
                  inputVariant="outlined"
                  format="DD/MM/YY"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  clearable
                  minDate={
                    formik.values?.startPrev
                      ? formik.values?.startPrev
                      : MIN_DATE
                  }
                  maxDate={MAX_DATE}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  onChange={date => handleDateChange('completionPrev', date)}
                  value={formik.values.completionPrev}
                  error={
                    formik.touched.completionPrev &&
                    Boolean(formik.errors.completionPrev)
                  }
                  helperText={
                    (formik.touched.completionPrev &&
                      formik.errors.completionPrev) ||
                    DEFAULT_HELPER_TEXT
                  }
                  className={classes.inputList}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  id="planNumber"
                  name="planNumber"
                  label="Plano"
                  variant="outlined"
                  helperText={
                    (formik.touched.planNumber && formik.errors.planNumber) ||
                    DEFAULT_HELPER_TEXT
                  }
                  onBlur={formik.handleBlur}
                  inputProps={{
                    type: 'number',
                  }}
                  onChange={event => {
                    const { value, name } = event.target;

                    if (value) {
                      formik.setFieldValue(name, Number(value));
                    } else {
                      formik.setFieldValue(name, null);
                    }
                  }}
                  value={formik.values.planNumber}
                  error={
                    formik.touched.planNumber &&
                    Boolean(formik.errors.planNumber)
                  }
                />
              </Grid>
            </Grid>
            {/* END ROW 2 */}

            {/* START ROW 3 */}
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <TextField
                  id="durationPrev"
                  name="durationPrev"
                  label="Duração prevista (HHH:MM)"
                  variant="outlined"
                  onChange={formik.handleChange}
                  value={formik.values.durationPrev}
                  error={
                    formik.touched.durationPrev &&
                    Boolean(formik.errors.durationPrev)
                  }
                  helperText={
                    (formik.touched.durationPrev &&
                      formik.errors.durationPrev) ||
                    DEFAULT_HELPER_TEXT
                  }
                  InputProps={{ inputComponent: InputDuration }}
                />
              </Grid>

              <Grid item xs={2}>
                <TextField
                  id="frequency"
                  name="frequency"
                  label="Periodicidade"
                  variant="outlined"
                  helperText={
                    (formik.touched.frequency && formik.errors.frequency) ||
                    OPTION
                  }
                  onBlur={formik.handleBlur}
                  inputProps={{
                    type: 'number',
                  }}
                  onChange={event => {
                    const { value, name } = event.target;

                    if (value) {
                      formik.setFieldValue(name, Number(value));
                    } else {
                      formik.setFieldValue(name, null);
                    }
                  }}
                  value={formik.values.frequency}
                  error={
                    formik.touched.frequency && Boolean(formik.errors.frequency)
                  }
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  id="observationField"
                  name="observationField"
                  label="Observação"
                  variant="outlined"
                  helperText={
                    (formik.touched.observationField &&
                      formik.errors.observationField) ||
                    OPTION
                  }
                  onBlur={formik.handleBlur}
                  inputProps={{
                    type: 'string',
                  }}
                  onChange={event => {
                    const { value, name } = event.target;
                    if (value && value.length) {
                      formik.setFieldValue(name, String(value));
                    } else {
                      formik.setFieldValue(name, null);
                    }
                  }}
                  value={formik.values.observationField}
                  error={
                    formik.touched.observationField &&
                    Boolean(formik.errors.observationField)
                  }
                />
              </Grid>
            </Grid>
            {/* END ROW 3 */}

            {/* INICIAL ROW 4 */}

            {/* END ROW 4 */}

            {/* START ROW 5 */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl
                  onChange={formik.handleChange}
                  error={formik.touched.outage && Boolean(formik.errors.outage)}
                >
                  <FormLabel className={classes.formLabel} component="legend">
                    <span className={classes.formInput}>
                      A atividade possui indisponibilidade de geração?
                    </span>
                    {queryOutageOptions?.isLoading && (
                      <span>
                        <CircularProgress color="inherit" size={25} />
                      </span>
                    )}
                  </FormLabel>
                  <RadioGroup id="outage" name="outage">
                    {outageOptions.map(({ value, label }) => (
                      <FormControlLabel
                        value={value}
                        control={<Radio color="primary" />}
                        label={label}
                      />
                    ))}
                    <FormHelperText id="outage">
                      {(formik.touched.outage && formik.errors.outage) ||
                        DEFAULT_HELPER_TEXT}
                    </FormHelperText>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            {/* END ROW 5 */}

            {/* START ROW 6 */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl
                  onChange={formik.handleChange}
                  error={
                    formik.touched.utilization &&
                    Boolean(formik.errors.utilization)
                  }
                >
                  <FormLabel className={classes.formLabel} component="legend">
                    <span className={classes.formInput}>
                      A atividade vai ser realizada em aproveitamento?
                    </span>
                    {queryUtilizationOptions?.isLoading && (
                      <span>
                        <CircularProgress color="inherit" size={25} />
                      </span>
                    )}
                  </FormLabel>
                  <RadioGroup id="utilization" name="utilization">
                    {utilizationOptions.map(({ value, label }) => (
                      <FormControlLabel
                        value={value}
                        control={<Radio color="primary" />}
                        label={label}
                      />
                    ))}
                    <FormHelperText id="utilization">
                      {(formik.touched.utilization &&
                        formik.errors.utilization) ||
                        DEFAULT_HELPER_TEXT}
                    </FormHelperText>
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            {/* END ROW 6 */}
          </Grid>
        </MuiPickersUtilsProvider>
      </form>
    ),
    [
      classes.bodyContent,
      classes.gridContent,
      classes.inputList,
      classes.formLabel,
      classes.formInput,
      formik,
      clusterOptions,
      installationOptions,
      activityOptions,
      fieldsSearchText?.activityNameOrId,
      typeMaintenanceOptions,
      defaultExpandedEquipments,
      equipmentOptions,
      queryEquipmentOptions?.isLoading,
      executorOptions,
      queryOutageOptions?.isLoading,
      outageOptions,
      queryUtilizationOptions?.isLoading,
      utilizationOptions,
      handleAutoCompleteChange,
      inputRenderer,
      queryClusterOptions?.isLoading,
      queryInstallationOptions?.isLoading,
      filter,
      handleFieldSearchTextChange,
      queryActivityOptions?.isLoading,
      queryMaintenanceTypeOptions?.isLoading,
      handleDateChange,
      clearFieldSearchTextChange,
      queryExecutorAllOptions,
      queryExecutorOptions?.isLoading,
    ],
  );

  return (
    <>
      {children}
      <ConfirmModal
        title="Confirmar Saída"
        message="É possível que as alterações feitas não sejam salvas."
        open={openConfirmModal}
        width="433px"
        onAccept={() => {
          onClose();
          setOpenConfirmModal(false);
        }}
        onDecline={() => setOpenConfirmModal(false)}
      />
      <Modal
        BackdropComponent={props => <Backdrop {...props} invisible />}
        open={open}
        onClose={onCloseModal}
        className={classes.presentation}
      >
        <div className={classes.modalPresentation}>
          <div className={classes.modalCard}>
            <div className={classes.modalHeader}>
              <div className={classes.modalHeaderItem}>
                <ArrowBackIcon
                  className={classes.headerIcon}
                  onClick={onCloseModal}
                />
                <Typography variant="h3" className={classes.headerTitle}>
                  Adicionar atividade
                </Typography>
              </div>
              <div className={classes.modalHeaderItem}>
                <ButtonWithLoading
                  disabled={!formik.dirty || !formik.isValid}
                  variant="contained"
                  color="primary"
                  type="submit"
                  form={FORM_ID}
                  isLoading={loading}
                >
                  Salvar
                </ButtonWithLoading>
              </div>
            </div>
            <div className={classes.modalBody}>{renderForm()}</div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default ModalAdd;
